import { useMutation, useQuery } from "@apollo/client";
import { Button, Col, Form, Layout, Row, Typography } from "antd";
import { Content } from "antd/es/layout/layout";
import ApplicationPanelFormContainer from "components/ApplicationPanel/styles";
import { Loading } from "components/Loading";
import { ADD_OR_UPDATE_PRODUCTS_MUTATION } from "graphql/manageLandingPage/mutations";
import { PRODUCTS_LIST, CATEGORIES_LIST } from "graphql/queries";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import ProductsFormList from "./ProductsFormList";

const { Title } = Typography;

const ProductsForm = () => {
  const { t } = useTranslation();

  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const errorMessageRef = useRef(null);
  const successMessageRef = useRef(null);

  const [submitting, setSubmitting] = useState(false);

  const {
    loading: productsListLoading,
    data: productsListData,
    refetch: productsListRefetch,
  } = useQuery(PRODUCTS_LIST, {
    variables: {},
  });

  const { loading: categoriesListLoading, data: categoriesListData } = useQuery(CATEGORIES_LIST);

  const [addOrUpdateProductsMutation] = useMutation(ADD_OR_UPDATE_PRODUCTS_MUTATION, {
    onCompleted: (data) => {
      setSubmitting(false);
      if (data.addOrUpdateProduct.success) {
        setErrorMessage(null);
        setSuccessMessage(t("dashboard.savedSuccessfullyMessage"));
        scrollToSuccess();
        setTimeout(() => {
          setSuccessMessage(null);
        }, 5000); // Clear success message after 5 seconds
        productsListRefetch();
      } else {
        setErrorMessage(data.addOrUpdateProduct.errors[0]);
        setSuccessMessage(null);
        scrollToError();
      }
    },
  });

  const onFinish = () => {
    setSubmitting(true);

    addOrUpdateProductsMutation({
      variables: { productsList: inputFields },
    });
  };

  const onFinishFailed = (errorInfo) => {
    // eslint-disable-next-line no-console
    console.log("Failed:", errorInfo);
  };

  const scrollToError = () => {
    if (errorMessageRef.current) {
      errorMessageRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const scrollToSuccess = () => {
    if (successMessageRef.current) {
      successMessageRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  // wait till the form initial values are calculated, as the form won't re-render.
  const [inputFieldsLoading, setInputFieldsLoading] = useState(true);
  const [inputFields, setInputFields] = useState([]);

  useEffect(() => {
    if (!productsListLoading) {
      setInputFieldsLoading(true);
      const data = productsListData.productsList.map((obj) => {
        return {
          id: obj.id,
          nameEn: obj.nameEn,
          nameAr: obj.nameAr,
          descriptionEn: obj.descriptionEn,
          descriptionAr: obj.descriptionAr,
          price: obj.price,
          active: obj.active,
          optionCategory: obj.options[0]?.optionCategory.id || "",
          options: obj.options.map((optionObj) => {
            return {
              valueEn: optionObj.valueEn,
              valueAr: optionObj.valueAr,
              id: optionObj.id,
              availableQuantity: optionObj.availableQuantity,
            };
          }),
        };
      });

      if (data.length === 0)
        data.push({
          nameEn: "",
          nameAr: "",
          descriptionEn: "",
          descriptionAr: "",
          picture: null,
          price: null,
          active: false,
          optionCategory: "",
          options: [
            {
              valueEn: "",
              valueAr: "",
              availableQuantity: 0,
            },
          ],
        });

      setInputFields(data);
      setInputFieldsLoading(false);
    }
  }, [productsListData, productsListLoading]);

  if (productsListLoading || inputFieldsLoading || categoriesListLoading) return <Loading />;

  return (
    <ApplicationPanelFormContainer>
      <Layout className="p-3 p-md-5 m-0">
        <Content>
          <Row gutter={[24, 0]}>
            <Col xs={24}>
              <Title
                className="mb-5 mt-0"
                style={{
                  fontSize: "45px",
                  whiteSpace: "break-spaces",
                }}
              >
                {t("dashboard.sideNav.productsTab")}
              </Title>
            </Col>
          </Row>

          <Form
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            className="row-col"
            scrollToFirstError={true}
          >
            <div ref={errorMessageRef}></div> {/* Error message ref */}
            {errorMessage && <p className="text-danger english-field">{errorMessage}</p>}
            <div ref={successMessageRef}></div> {/* Success message ref */}
            {successMessage && <p className="text-success">{successMessage}</p>}
            <ProductsFormList
              inputFields={inputFields}
              setInputFields={setInputFields}
              data={productsListData.productsList}
              categoriesListData={categoriesListData}
            />
            <Row gutter={[24, 0]} justify="center">
              <Col xs={24}>
                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    style={{ width: "100%" }}
                    loading={submitting}
                    disabled={submitting}
                  >
                    {t("dashboard.applicationPanelForm.saveBtn")}
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Content>
      </Layout>
    </ApplicationPanelFormContainer>
  );
};

export default ProductsForm;
